/**
 * Dashboard KPI visibility preferences.
 * Persisted per role in localStorage. Keys never leave the browser.
 *
 * Shape:  { visible: string[], section_overrides?: Record<string, string> }
 *         `visible`           — ordered list of KPI ids the user wants to see
 *         `section_overrides` — per-tile section override (drop from Overview → Operational)
 *         null (missing entry) — use the default set for that role.
 *
 * `KPI_CATALOG` is the single source of truth for tile ids + labels + default
 * sections. `SECTION_ORDER` fixes the render order of sections on screen.
 */

const STORAGE_PREFIX = "iris.dashboard_prefs.v1.";

export const SECTION_ORDER = ["Overview", "Personnel", "Operational"];

export const KPI_CATALOG = {
  // Core stats from /dashboard/stats
  active_sites:        { label: "Active Sites",        section: "Overview" },
  in_progress_shifts:  { label: "Shifts On-Duty",      section: "Overview" },
  open_incidents:      { label: "Open Incidents",      section: "Overview" },
  monthly_revenue:     { label: "Contracted MRR",      section: "Overview", adminOnly: true },
  active_guards:       { label: "Active Officers",       section: "Personnel" },
  total_guards:        { label: "Total Officers",        section: "Personnel" },
  // Operational metrics from /metrics/operational
  coverage_pct:        { label: "Coverage %",          section: "Operational" },
  unrealized_revenue:  { label: "Unrealized Revenue",  section: "Operational", adminOnly: true },
  unrealized_hours:    { label: "Unrealized Hours",    section: "Operational" },
  tardy_pct:           { label: "Tardy %",             section: "Operational" },
  early_out_pct:       { label: "Early Clock-outs %",  section: "Operational" },
  no_show_count:       { label: "No-Show Officers",      section: "Operational", linkTo: "/reports/no-shows" },
  incidents_all:       { label: "Incidents",           section: "Operational" },
  maintenance_all:     { label: "Maintenance Requests", section: "Operational" },
  tours_scanned:       { label: "Tours Scanned",       section: "Operational" },
  supervisor_visits:   { label: "Supervisor Visits",   section: "Operational" },
  missed_tour_alerts_today: {
    label: "Missed Tour Alerts (Today)",
    section: "Operational",
    linkTo: "/tardies?tab=missed_tour_stops&when=today",
  },
};

export const DEFAULT_VISIBLE_BY_ROLE = {
  admin: [
    "active_sites", "in_progress_shifts", "open_incidents", "monthly_revenue",
    "active_guards", "total_guards",
    "coverage_pct", "unrealized_revenue", "tardy_pct", "no_show_count",
    "missed_tour_alerts_today",
  ],
  supervisor: [
    "active_sites", "in_progress_shifts", "open_incidents",
    "active_guards",
    "coverage_pct", "tardy_pct", "early_out_pct", "no_show_count",
    "missed_tour_alerts_today",
  ],
  operations_manager: [
    "active_sites", "in_progress_shifts", "open_incidents",
    "active_guards", "total_guards",
    "coverage_pct", "tardy_pct", "early_out_pct", "no_show_count",
    "missed_tour_alerts_today",
  ],
  chief_security_officer: [
    "active_sites", "in_progress_shifts", "open_incidents",
    "active_guards", "total_guards",
    "coverage_pct", "tardy_pct", "no_show_count", "incidents_all",
    "missed_tour_alerts_today",
  ],
};

function keyFor(role) {
  return `${STORAGE_PREFIX}${role || "default"}`;
}

export function loadKpiPrefs(role) {
  try {
    const raw = localStorage.getItem(keyFor(role));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.visible)) return null;
    return {
      visible: parsed.visible,
      section_overrides: (parsed.section_overrides && typeof parsed.section_overrides === "object")
        ? parsed.section_overrides
        : {},
      section_labels: (parsed.section_labels && typeof parsed.section_labels === "object")
        ? parsed.section_labels
        : {},
    };
  } catch {
    return null;
  }
}

export function saveKpiPrefs(role, visible, sectionOverrides = {}, sectionLabels = {}) {
  try {
    localStorage.setItem(
      keyFor(role),
      JSON.stringify({
        visible,
        section_overrides: sectionOverrides || {},
        section_labels: sectionLabels || {},
      }),
    );
  } catch {
    /* localStorage disabled — silently degrade */
  }
}

export function resetKpiPrefs(role) {
  try {
    localStorage.removeItem(keyFor(role));
  } catch {
    /* no-op */
  }
}

/** Resolve the effective visible list + section overrides + labels for a role. */
export function resolveKpiState(role) {
  const prefs = loadKpiPrefs(role);
  if (prefs) return {
    visible: prefs.visible,
    sectionOverrides: prefs.section_overrides || {},
    sectionLabels: prefs.section_labels || {},
  };
  return {
    visible: DEFAULT_VISIBLE_BY_ROLE[role] || DEFAULT_VISIBLE_BY_ROLE.admin,
    sectionOverrides: {},
    sectionLabels: {},
  };
}

/** Back-compat: resolve just the visible list. */
export function resolveVisibleKpis(role) {
  return resolveKpiState(role).visible;
}

/** Effective section for a tile — honours the per-user override. */
export function sectionFor(kpiId, sectionOverrides = {}) {
  const override = sectionOverrides?.[kpiId];
  if (override && SECTION_ORDER.includes(override)) return override;
  return KPI_CATALOG[kpiId]?.section || "Operational";
}

/** Display label for a section — honours the per-user rename. */
export function sectionLabelFor(sectionKey, sectionLabels = {}) {
  const custom = sectionLabels?.[sectionKey];
  return (custom && String(custom).trim()) || sectionKey;
}

/** True when the KPI is admin-only AND the caller isn't an admin/super. */
export function isKpiAllowedForRole(kpiId, role) {
  const meta = KPI_CATALOG[kpiId];
  if (!meta) return false;
  if (meta.adminOnly && !(role === "admin" || role === "super_admin")) return false;
  return true;
}
